/**
 * TweakSlider — custom styled slider for performance surfaces.
 *
 * Thick track with colored fill, rectangular thumb handle.
 * Div-based for full styling control. Supports drag + double-click text edit.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { formatValue } from '../utils';

interface TweakSliderProps {
  value: number;
  min: number;
  max: number;
  step?: number;
  color: string;
  trackColor?: string;
  onChange: (value: number) => void;
  label?: string;
}

const TRACK_HEIGHT = 8;
const THUMB_WIDTH = 14;
const THUMB_HEIGHT = 24;

function snapToStep(val: number, min: number, max: number, step: number): number {
  const clamped = Math.max(min, Math.min(max, val));
  return Math.round((clamped - min) / step) * step + min;
}

export function TweakSlider({ value, min, max, step = 1, color, trackColor = '#333', onChange, label }: TweakSliderProps) {
  const [localValue, setLocalValue] = useState(value);
  const [isDragging, setIsDragging] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editText, setEditText] = useState('');
  const trackRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Sync local value to prop when not dragging
  useEffect(() => {
    if (!isDragging) setLocalValue(value);
  }, [value, isDragging]);

  // Focus input when entering edit mode
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const fraction = max === min ? 0 : (localValue - min) / (max - min);

  const valueFromX = useCallback((clientX: number) => {
    const track = trackRef.current;
    if (!track) return localValue;
    const rect = track.getBoundingClientRect();
    const frac = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    return snapToStep(min + frac * (max - min), min, max, step);
  }, [min, max, step, localValue]);

  const handlePointerDown = useCallback((e: React.PointerEvent) => {
    e.preventDefault();
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    setIsDragging(true);
    const v = valueFromX(e.clientX);
    setLocalValue(v);
    onChange(v);
  }, [valueFromX, onChange]);

  const handlePointerMove = useCallback((e: React.PointerEvent) => {
    if (!isDragging) return;
    const v = valueFromX(e.clientX);
    setLocalValue(v);
    onChange(v);
  }, [isDragging, valueFromX, onChange]);

  const handlePointerUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  const commitEdit = () => {
    const parsed = parseFloat(editText);
    if (!isNaN(parsed)) {
      onChange(snapToStep(parsed, min, max, step));
    }
    setIsEditing(false);
  };

  if (isEditing) {
    return (
      <div style={{ width: '100%' }}>
        <input
          ref={inputRef}
          type="text"
          className="nodrag nopan"
          value={editText}
          onChange={e => setEditText(e.target.value)}
          onBlur={commitEdit}
          onKeyDown={e => {
            if (e.key === 'Enter') commitEdit();
            if (e.key === 'Escape') setIsEditing(false);
          }}
          style={{
            width: '100%',
            fontFamily: 'monospace',
            fontSize: 10,
            color: '#e5e7eb',
            backgroundColor: '#1a1a2e',
            border: `1px solid ${color}`,
            borderRadius: 2,
            padding: '1px 4px',
            outline: 'none',
          }}
        />
      </div>
    );
  }

  return (
    <div
      style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 2 }}
      onDoubleClick={() => {
        setEditText(String(localValue));
        setIsEditing(true);
      }}
    >
      {label && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
          <span style={{ color: '#9ca3af', fontSize: 10 }}>{label}</span>
          <span style={{ color: '#e5e7eb', fontFamily: 'monospace', fontSize: 10 }}>
            {formatValue(localValue)}
          </span>
        </div>
      )}
      <div
        ref={trackRef}
        className="nodrag nopan"
        style={{
          position: 'relative',
          width: '100%',
          height: THUMB_HEIGHT,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          touchAction: 'none',
        }}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
      >
        {/* Track background */}
        <div style={{
          position: 'absolute',
          left: 0,
          right: 0,
          height: TRACK_HEIGHT,
          borderRadius: 2,
          backgroundColor: trackColor,
        }} />
        {/* Filled portion */}
        <div style={{
          position: 'absolute',
          left: 0,
          width: `${fraction * 100}%`,
          height: TRACK_HEIGHT,
          borderRadius: 2,
          backgroundColor: color,
        }} />
        {/* Thumb */}
        <div style={{
          position: 'absolute',
          left: `calc(${fraction * 100}% - ${THUMB_WIDTH / 2}px)`,
          width: THUMB_WIDTH,
          height: THUMB_HEIGHT,
          borderRadius: 2,
          backgroundColor: color,
        }} />
      </div>
      {!label && (
        <span style={{
          color: '#e5e7eb',
          fontFamily: 'monospace',
          fontSize: 10,
          textAlign: 'right',
        }}>
          {formatValue(localValue)}
        </span>
      )}
    </div>
  );
}
